/** @jsxImportSource @emotion/react */
import React, { forwardRef } from 'react';
import { Field } from 'react-final-form';
import PropTypes from 'prop-types';
import Moment from 'moment';
import MomentLocalizer from 'react-widgets-moment';
import DatePicker from "react-widgets/DatePicker";
import TimeInput from "react-widgets/TimeInput";
import { DateLocalizer, NumberLocalizer, Localization } from 'react-widgets/IntlLocalizer';
import { Label } from 'reactstrap';
import { css } from '@emotion/react'
import TimeFormatter from '../../commons/TimeFormatter';


Moment.locale('hu')

const momentLocalizer = new MomentLocalizer(Moment)

const RFField = forwardRef((props, ref) => {
  const validate = (value) => {
    if (props.required && !value && !props.forcedValue) {
      return 'Kötelező mező!';
    }
    // null érték helyett üres stringet vizsgál értékként
    let regexTestValue = value || '';
    if (props.regex && props.regex.length == 2) {
      const regexp = new RegExp(props.regex[0]);
      // üres mező esetén ne adjon hibát a mezőre, egyébként ha nem sikerül a regex -> hiba
      if (value && !regexp.test(regexTestValue)) {
        return props.regex[1];
      }
    }
    return props.validate ? props.validate(value) : undefined;
  };

  let inputElement = (inputProps, props, meta, ref) => {
    if (props.type == "textarea")
      return <textarea {...inputProps} className={(props.className || "") + ' form-control' + (meta.touched && meta.error ? " is-invalid" : "")} ref={ref} disabled={props.disabled} placeholder={props.placeholder} style={props.style} />
    let input = <input
      {...inputProps}
      value={props.forcedValue || inputProps.value}
      // className={meta.touched && meta.error ? ("is-invalid form-control " + (props.className || "")) : ("form-control " + (props.className || ""))}
      className={(props.className || "") + " form-control" + (meta.touched && meta.error ? " is-invalid" : "") + " col-order-6"}
      ref={ref}
      disabled={props.disabled}
      placeholder={props.placeholder}
      style={{ ...props.style, marginLeft: 0 }}
      id={props.id || props.name}
      autoComplete={props.autoComplete}
      onKeyDown={props.onKeyDown}
    />;

    let append = <div className='input-group-text'>
      {props.append}
    </div>
    let prepend = <div className='input-group-text'>
      {props.prepend}
    </div>;
    if (props.prepend || props.append) {
      return <div className='input-group'>
        {props.prepend && prepend}
        {input}
        {props.append && append}
      </div>;
    }
    return input;
  }

  const formatDate = (value) => {
    if (props.type === 'date' || props.type === 'datetime' || props.type === 'datetime-local') {
      value = new Date(value).toLocaleString('hu-HU');
      if (props.type === 'date') {
        if (props.formatDate) {
          value = TimeFormatter.format(value, props.formatDate);
        } else {
          value = TimeFormatter.formatDate(value);
        }
      } else if (props.type === 'datetime' || props.type === 'datetime-local') {
        if (props.formatDate) {
          value = TimeFormatter.format(value, props.formatDate);
        } else {
          value = TimeFormatter.formatFullDate(value);
        }
      }
    }
    return value;
  }

  const formatValueToString = (value) => {
    if (!value)
      value = "";
    else
      value += "";
    return value;
  }

  const numberAndLengthFormatting = (value) => {
    // Különböző formázások paraméterek alapján
    if (props.numberOnly) {
      value = value.replace(/[^\d.]/g, "");
    }
    if (props.numberOnlyWhole) {
      value = value.replace(/[^\d]/g, "");
    }
    if (props.maxLength) {
      let maxLength = props.maxLength;
      if (typeof props.maxLength === "string")
        maxLength = Number(props.maxLength);
      value = value.substring(0, maxLength);
    }
    return value;
  }

  // Itt lehet a formázást megoldani, a visszaadott érték kerül beírásra az input mezőbe, ergo a mutatott érték
  // Ahhoz hogy a mutatott és a tárolt érték is ugyanaz legyen a format és parse metódusban is meg kell csinálni az adott műveletet
  const format = (value) => {
    value = formatValueToString(value);
    // Ha van format metódus megadva kívül, akkor az fut meg először
    if (props.format && typeof props.format === 'function') {
      value = props.format(value);
    }

    value = formatDate(value);
    value = numberAndLengthFormatting(value);

    return value;
  }

  // Itt lehet megadni a formázását a formon belül tárolt értéknek, ergo az ami a háttérben a formValues lesz
  const parse = (value) => {
    value = formatValueToString(value);
    // Ha van parse metódus megadva kívül, akkor az fut meg először
    if (props.parse && typeof props.parse === 'function') {
      value = props.parse(value);
    }

    value = formatDate(value);
    value = numberAndLengthFormatting(value);

    return value;
  }

  const renderLabelWithRequiredAsterisk = (labelProps) => {
    if (props.label)
      return <Label {...labelProps}>{props.label}{(props.required && props.label.charAt(props.label.length - 1) != '*' ? " *" : "")}</Label>
      // return props.label + (props.required && props.label.charAt(props.label.length - 1) != '*' ? " *" : "");
  }

  const FormGroupAdapter = ({ input, meta, inputOnChange }) => {
    // Egyedi eset miatt kell, cikkSelect-ben display: none van használva, és ilyenkor a teljes div-et el kell rejteni (alapba csak a belső input kapja meg a style-t)
    let styleObj;
    if (props.style && props.style.display == 'none')
        styleObj = { display: 'none' };

    const inputProps = {
        ...input,
        onChange: (e) => {
          const target = e.target;
          // parse kell, mert az onChange a parse előtt kerül meghívásra, és ez még a formázatlan érték
          const value = parse(target.type === "checkbox" ? target.checked : (target.value !== '' ? target.value : props.defaultValue));
          // console.log('RFFIELD VALUE: ', value);
          input.onChange(value);
          inputOnChange && inputOnChange(e, value);
        },
    };

      if (props.children) {
        return (
          <>
            {props.children}
            {meta.touched && meta.error && <span>{meta.error}</span>}
          </>
        );
      }
      let errorElement = !props.hideErrorMessage && meta.touched && meta.error && <div style={{ textAlign: 'end' }} className="col">{meta.error}</div>;
      if (input.type === 'select') {
        return (
          <div className={(meta.touched && meta.error && "text-danger") + " position-relative form-group " + (props.className || "")} style={props.selectContainerStyles}>
            <div className='row no-gutters justify-content-between'>
              <div className='col-auto row no-gutters'>
                <div className='col-auto'>
                  {renderLabelWithRequiredAsterisk()}
                </div>
                <div className='col-auto'>
                  {props.tooltip}
                </div>
              </div>
              <div className='col-auto justify-content-end'>
                {errorElement}
              </div>
            </div>
            <select {...inputProps} ref={ref} disabled={props.disabled} className={meta.touched && meta.error ? ("is-invalid form-control " + (props.className || "")) : ("form-control " + (props.className || ""))} style={props.style}>
              {props.defaultSelect}
              {props.selectOptions}
            </select>
          </div>
        );
      }

      return (
        <div style={styleObj} className={(meta.touched && meta.error && "text-danger") + " position-relative " + (!(props.hideFormGroup ?? false) && 'form-group') + (props.className || "")}>
          {props.label ?
            <>
              <div className='row no-gutters justify-content-between'>
                <div className='col-auto row no-gutters'>
                  <div className='col-auto'>
                    {renderLabelWithRequiredAsterisk()}
                  </div>
                  <div className='col-auto'>
                    {props.tooltip}
                  </div>
                </div>
                {errorElement}
              </div>
              <div>
                {inputElement(inputProps, props, meta, ref)}
              </div>
            </>
            :
            <>
              <div className='row justify-content-end'>
                {errorElement}
              </div>
              {inputElement(inputProps, props, meta, ref)}
            </>
          }
        </div>
      );
  };


if (props.type == 'checkbox' || props.type == 'radio') {
  let fieldElement;
  // A kettő <Field />-t nem lehet összevonni, mert ha a value bármilyen módon töltve van a checkbox esetén (még value={null} is rossz),
  // akkor már [false]-t és []-t ad vissza válaszként igaz/hamis-ra a true/false helyett
  if (props.type === 'checkbox') {
    let labelOrderNumber = props.labelFirst ? 5 : 7;
    fieldElement = <Field
      type='checkbox'
      component="input"
      onClick={props.onClick || props.onChange}
      name={props.name}
      id={props.id || props.name}
      initialValue={props.value || props.checked}
      disabled={props.disabled}
      className="col-auto order-6"
      style={{ marginLeft: 5, marginRight: 5, marginTop: 2, ...props.styleCheckbox }}
      />;
      // Duplán kell a row no-gutters, hogy ne lehessen az input-ot a label melletti üres részen aktiválni,
      // a sorrendet meg lehessen adni a label-ben, valamint, hogy a soron belüli marginTop működjön
      return (
        <div className='row no-gutters'>
          <label className='row no-gutters col-auto mb-0'>
            {fieldElement}
            <div className={`col-auto order-${labelOrderNumber}`}>{props.label}{props.tooltip}</div>
          </label>
        </div>
      )
  } else {
    fieldElement = <Field
      type='radio'
      component="input"
      name={props.name}
      onClick={props.onClick || props.onChange}
      value={props.value}
      disabled={props.disabled}
      style={{ marginTop: 2, marginRight: 5 }}
      className="col-auto"
      checked={props.checked}
      />;
      return (
        <div className='row no-gutters'>
          <label className='row no-gutters mb-0 col-auto'>
            {fieldElement}
            {props.label}
            {props.tooltip}
          </label>
        </div>
      )
  }
}

  const formatter = Intl.DateTimeFormat('hu-hu');
  const todayStyleSelector = `& .rw-cell[title^="${formatter.format(new Date())}"]:not([aria-selected="true"])`;

  return ((props.type === 'date' || props.type === 'datetime' || props.type === 'datetime-local' || props.type === 'time') ? (
    <React.Fragment>
      <div className='row no-gutters'>
        <div className='col-auto'>
          {props.label && renderLabelWithRequiredAsterisk()
            // <Label
            //   for={props.id}
            //   className={props.labelClass}
            //   hidden={props.labelHidden}
            //   size={props.size}
            //   {...props.labelCol}
            //   {...props.labelAttrs}
            // >
            //   {props.label}
            // </Label>
          }
        </div>
        <div className='col-auto'>
          {props.tooltip}
        </div>
      </div>
      {props.type === 'time' ? (
        <TimeInput
          name={props.name || props.id}
          // date={momentLocalizer}
          id={props.id || props.name}
          ref={ref}
          // formatDate={props.formatDate}
          timePrecision={props.timePrecision}
          onChange={props.onChange}
          onSelect={props.onSelect}
          value={props.value}
          valueFormat={format}
          disabled={props.disabled}
          onBlur={props.onBlur}
          style={{ marginBottom: 7, ...props.style }}
        />
      ) : (
        <DatePicker
          name={props.name || props.id}
          date={momentLocalizer}
          id={props.id || props.name}
          ref={ref}
          time={props.time}
          formatDate={props.formatDate}
          timePrecision={props.timePrecision}
          //valueDisplayFormat="YYYY-MM-DD"
          includeTime={props.type === 'datetime' || props.type === 'datetime-local'}
          onChange={props.onChange}
          onSelect={props.onSelect}
          value={props.value}
          valueFormat={format}
          disabled={props.disabled}
          onBlur={props.onBlur}
          defaultOpen={props.defaultOpen}
          onKeyPress={props.onKeyPress}
          // valueFormat={"YYYYDD"}
          // style={{ marginBottom: 7, ...props.style }}
          css={css({
            marginBottom: '7px',
            ...props.style,
            [todayStyleSelector]: {
              backgroundColor: '#e9ecef !important',
            }
          })}
          messages={{
            moveToday: "Ma",
            moveBack: "Visszalépés",
            moveForward: "Előrelépés",
            emptyList: "Nincs elem a listában",
            emptyFilter: "Szűrő feltételeknek nincs eredménye",
          }}
        />
      )}
       
    </React.Fragment>
  ) : (
    <Field
      name={props.name || props.id}
      id={props.id || props.name}
      ref={ref}
      render={FormGroupAdapter}
      inputOnChange={props.onChange}
      type={props.type || 'text'}
      validate={validate}
      initialValue={props.value}
      value={props.value}
      format={format}
      parse={parse}
      multiple={props.multiple}
      disabled={props.disabled}
      autoComplete={props.autoComplete}
    />
  ));
});

RFField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  component: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  validate: PropTypes.func,
  children: PropTypes.node,
  defaultSelect: PropTypes.node,
  selectOptions: PropTypes.node,
  value: PropTypes.any,
  // Akkor kell használni a value helyett, ha már felhasználó által aktivált mező értékét kell prop-pal megváltoztatni
  forcedValue: PropTypes.any,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  regex: PropTypes.array,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  format: PropTypes.func,
  // Formázza a dátumot pl.: YYYY-MM-DD vagy YYYY-MM-DDTHH:mm
  formatDate: PropTypes.string,
  // Csak számokat és pontot enged
  numberOnly: PropTypes.bool,
  // Csak számokat enged
  numberOnlyWhole: PropTypes.bool,
  multiple: PropTypes.bool,
  style: PropTypes.object,
  // Igaz esetén a checkbox label-je meg fogja előzni magát a checkboxot
  labelFirst: PropTypes.bool,
};

export default RFField;
